import React, { useEffect, useState } from "react";
import type { CourseProgressSummary } from "../appTypes";
import Certificate from "./Certificate";
import "./CertificatesModal.css";

interface CertificatesModalProps {
  open: boolean;
  onClose: () => void;
  courses: CourseProgressSummary[];
  userName?: string;
  programName?: string;
  onIssueCertificate?: (programName: string) => Promise<void>;
}

const Medal = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="9" r="5.2" /><path d="M8.5 13.4 7 21l5-2.4L17 21l-1.5-7.6" />
  </svg>
);

const CertificatesModal: React.FC<CertificatesModalProps> = ({ open, onClose, courses, userName, programName = "Formação Blue Capital", onIssueCertificate }) => {
  const [certOpen, setCertOpen] = useState(false);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  const totals = courses.reduce(
    (a, c) => ({ w: a.w + c.watchedLessons, t: a.t + c.totalLessons }),
    { w: 0, t: 0 },
  );
  const overall = totals.t ? Math.round((totals.w / totals.t) * 100) : 0;
  const left = Math.max(0, totals.t - totals.w);
  const complete = totals.t > 0 && left === 0;

  async function handleOpenCertificate() {
    if (issuing) return;
    setIssuing(true);
    try {
      await onIssueCertificate?.(programName);
    } finally {
      setIssuing(false);
      setCertOpen(true);
    }
  }

  return (
    <>
      <div className="certs-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="certs-modal" role="dialog" aria-modal="true" aria-label="Seu certificado">
          <button className="certs-close" onClick={onClose} aria-label="Fechar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>

          <div className="certs-hero">
            <div className="certs-ring" style={{ ["--pct" as string]: overall } as React.CSSProperties}>
              <span className="certs-ring-num">{overall}<i>%</i></span>
            </div>
            <div className="certs-hero-txt">
              <span className="certs-eyebrow"><Medal />Certificado</span>
              <h2>Certificado de conclusão</h2>
              <p>Um único certificado, liberado quando você concluir <b>100% de todo o material</b>.</p>
            </div>
          </div>

          <div className={`certs-cert ${complete ? "is-ready" : "is-locked"}`}>
            <span className="certs-cert-ic">
              {complete ? <Medal /> : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
              )}
            </span>
            <div className="certs-cert-body">
              <strong>{complete ? "Parabéns! Seu certificado está pronto." : "Conclua tudo para liberar"}</strong>
              {complete ? (
                <small>Gerado com o seu nome — baixe em PNG e compartilhe.</small>
              ) : (
                <>
                  <small>Faltam <b>{left}</b> {left === 1 ? "aula" : "aulas"} para concluir 100% do material.</small>
                  <div className="certs-cert-bar"><i style={{ width: `${overall}%` }} /></div>
                </>
              )}
            </div>
            {complete ? (
              <button type="button" className="certs-cert-cta" onClick={handleOpenCertificate} disabled={issuing}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" /></svg>
                {issuing ? "Gerando..." : "Baixar certificado"}
              </button>
            ) : (
              <span className="certs-cert-pct">{overall}%</span>
            )}
          </div>

          {courses.length ? (
            <>
              <div className="certs-sub">Seu progresso por curso</div>
              <div className="certs-list">
                {courses.map((c) => {
                  const done = c.totalLessons > 0 && c.progressPercent >= 100;
                  const clh = Math.max(0, c.totalLessons - c.watchedLessons);
                  return (
                    <div key={c.courseId} className={`certs-row ${done ? "is-done" : ""}`}>
                      <span className="certs-row-ic">
                        {done ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="m5 12 5 5 9-11" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="8.5" /></svg>
                        )}
                      </span>
                      <div className="certs-row-body">
                        <strong>{c.title}</strong>
                        <div className="certs-row-bar"><i style={{ width: `${Math.min(100, c.progressPercent)}%` }} /></div>
                        <small>
                          {done
                            ? "Concluído"
                            : c.totalLessons
                              ? `${c.progressPercent}% · faltam ${clh} de ${c.totalLessons} aulas`
                              : "Aulas em breve"}
                        </small>
                      </div>
                      {!done && c.totalLessons ? (
                        <a className="certs-row-link" href={`#/curso/${c.courseId}`} onClick={onClose}>Continuar</a>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="certs-empty">
              Você ainda não tem cursos. Comece um curso para avançar rumo ao seu certificado.
            </div>
          )}
        </div>
      </div>

      <Certificate
        open={certOpen}
        studentName={userName || "Aluno"}
        courseName={programName}
        onClose={() => setCertOpen(false)}
      />
    </>
  );
};

export default CertificatesModal;
